import type { ChangeEvent, RefObject } from 'react';
import { use } from 'react';
import { useTranslation } from 'react-i18next';

import { AbilityContext } from '../../ability';
import { ErrorMessage } from '../common/ErrorMessage';
import { BatchUploadReady } from './BatchUploadReady';
import { BatchUploadResults } from './BatchUploadResults';
import type { useBatchUpload } from './use-batch-upload';

function DropZone({
  inputRef,
  isReplacing,
  onFileChange,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  isReplacing: boolean;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('photoUpload.dropZoneLabel')}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const dt = new DataTransfer();
        Array.from(e.dataTransfer.files).forEach((f) => dt.items.add(f));
        if (inputRef.current) {
          inputRef.current.files = dt.files;
          inputRef.current.dispatchEvent(
            new Event('change', { bubbles: true }),
          );
        }
      }}
      className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-rule bg-bg-soft p-6 text-center [transition:border-color_150ms_ease,background_150ms_ease]"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".zip,image/jpeg,image/png,.jpg,.jpeg,.png"
        multiple
        onChange={onFileChange}
        className="hidden"
      />
      <svg
        className="size-9 text-muted-soft"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p className="text-sm font-medium text-ink">
        {isReplacing ? t('batchUpload.dropNew') : t('batchUpload.dropzone')}
      </p>
      <p className="text-xs text-muted">{t('batchUpload.dropzoneHint')}</p>
    </div>
  );
}

/** Extracting and validating: a spinner over one line of progress. */
function PhaseSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10">
      <svg
        className="size-8 animate-spin text-blue"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <p className="text-sm text-muted">{label}</p>
    </div>
  );
}

function UploadProgress({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  const { t } = useTranslation();
  const ratio = current / total;
  return (
    <div className="space-y-3 py-4">
      <div className="flex items-center justify-between text-sm text-muted">
        <span>{t('batchUpload.uploading', { current, total })}</span>
        <span className="font-semibold text-ink">
          {Math.round(ratio * 100)}%
        </span>
      </div>
      <div className="h-2.5 w-full overflow-hidden bg-ink/8">
        <div
          className="h-2.5 bg-blue transition-[width] duration-300"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    </div>
  );
}

interface BatchUploadProps {
  /** The flow, owned by the page so its stepper reads the same state. */
  flow: ReturnType<typeof useBatchUpload>;
}

export function BatchUpload({ flow }: BatchUploadProps) {
  const { t } = useTranslation();
  const ability = use(AbilityContext);
  const canBatch = ability.can('upload', 'Batch');
  const { phase, pending, validation, results, error } = flow;

  if (!canBatch) {
    return (
      <p className="text-sm text-muted-soft italic">
        {t('batchUpload.noPermission')}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* Drop zone */}
      {(phase === 'idle' || phase === 'ready') && (
        <DropZone
          inputRef={flow.fileInputRef}
          isReplacing={phase === 'ready'}
          onFileChange={(e) => {
            void flow.handleFileChange(e);
          }}
        />
      )}

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Extracting */}
      {phase === 'extracting' && (
        <PhaseSpinner
          label={t('batchUpload.extracting', { count: flow.extractCount })}
        />
      )}

      {/* Validating */}
      {phase === 'validating' && (
        <PhaseSpinner
          label={t('batchUpload.checkingUsers', { count: pending.length })}
        />
      )}

      {phase === 'ready' && validation.length > 0 && (
        <BatchUploadReady
          pending={pending}
          validation={validation}
          confirmEmployeesOpen={flow.confirmEmployeesOpen}
          setConfirmEmployeesOpen={flow.setConfirmEmployeesOpen}
          onConfirmUpload={() => {
            void flow.handleConfirmUpload();
          }}
          onReset={flow.handleReset}
        />
      )}

      {/* Uploading */}
      {phase === 'uploading' && (
        <UploadProgress
          current={flow.uploadProgress.current}
          total={flow.uploadProgress.total}
        />
      )}

      {/* Results */}
      {phase === 'done' && results.length > 0 && (
        <BatchUploadResults results={results} onReset={flow.handleReset} />
      )}
    </div>
  );
}
